/**
 * Logger utility for URL-based logging control
 * 
 * Logging is ENABLED for:
 *   - localhost / 127.0.0.1 (local development)
 *   - portal-t.mwcloud.no (test environment)
 *   - Any URL containing '-t.' (test environments)
 * 
 * Logging is DISABLED for:
 *   - portal.mwcloud.no (production)
 *   - Any other production URL
 * 
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.error('Something went wrong', error);
 *   logger.warn('Warning message');
 *   logger.info('Info message');
 *   logger.debug('Debug message');
 */

// Helper to detect localhost
export const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Helper to detect test environment URL (contains -t. like portal-t.mwcloud.no or api-t.mwcloud.no)
export const isTestEnvironment = typeof window !== 'undefined' && 
  window.location.hostname.includes('-t.');

// Helper to detect production URL (portal.mwcloud.no but NOT portal-t.mwcloud.no)
export const isProductionUrl = typeof window !== 'undefined' && 
  window.location.hostname === 'portal.mwcloud.no';

// Show logs ONLY on localhost and test environments, hide everywhere else (including production)
const shouldShowLogs = isLocalhost || isTestEnvironment;

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface Logger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

const noop = () => {};

/**
 * Create a log function that only logs on localhost and test environments
 * In production, logs are suppressed (can be extended to send to monitoring service)
 */
const createLogFn = (level: LogLevel): ((...args: unknown[]) => void) => {
  if (!shouldShowLogs) {
    // In production: suppress logs
    // TODO: For errors, consider sending to Application Insights
    return noop;
  }

  switch (level) {
    case 'debug':
      return console.debug.bind(console);
    case 'info':
      return console.info.bind(console);
    case 'warn':
      return console.warn.bind(console);
    case 'error':
      return console.error.bind(console);
    default:
      return console.log.bind(console);
  }
};

export const logger: Logger = {
  debug: createLogFn('debug'),
  info: createLogFn('info'),
  warn: createLogFn('warn'),
  error: createLogFn('error'),
};

export default logger;

